import React, { useEffect, useMemo, useState, useRef } from "react"
import { gsap } from "gsap"
import PropTypes from "prop-types"
import { Link, useLocation } from "react-router-dom"
import { FaAngleDoubleRight, FaPauseCircle, FaPlayCircle } from "react-icons/fa"

import {
  animationSettings,
  progressSettings,
  totalCycle,
  years,
} from "../services/timelineService"

import FlyingIn from "../components/Timeline/FlyingIn"
import Progress from "../components/Timeline/ProgressBar/Progress"
import Music from "../components/Music"
import HeroVideo from "../components/Timeline/HeroVideo"
import List from "../components/List"
import NavBar from "../components/NavBar"

import FiveYearRibbon from "../images/ribbons/5year.svg"
import TenYearRibbon from "../images/ribbons/10year.svg"
import FifteenYearRibbon from "../images/ribbons/15year.svg"
import TwentyYearRibbon from "../images/ribbons/20year.svg"
import TwentyfiveYearRibbon from "../images/ribbons/25year.svg"
import ThirtyYearRibbon from "../images/ribbons/30year.svg"
import ThirtyfiveYearRibbon from "../images/ribbons/35year.svg"
import FortyYearRibbon from "../images/ribbons/40year.svg"
import FortyfiveYearRibbon from "../images/ribbons/45year.svg"

const people = require("../fixtures/people.json")
const sampleData = require("../fixtures/content.json")
const Timeline = () => {
  const [currentSegment, setCurrentSegment] = useState(0)
  const [musicPlaying, setMusicPlaying] = useState(false)
  const [cleaningUp, setCleaningUp] = useState(false)
  const [vidPlayer, setVidPlayer] = useState({})
  const [vidPlayerPaused, setVidPlayerPaused] = useState(vidPlayer.paused)
  const [showAdvancer, setShowAdvancer] = useState(false)
  const [showControls, setShowControls] = useState(true)

  let heroTl = useMemo(() => gsap.timeline().pause(), [])
  let componentTl = useMemo(
    () => gsap.timeline().timeScale(animationSettings.timeScale).pause(),
    []
  )

  const setPlayer = (childPlayer) => {
    setVidPlayer(childPlayer)
  }

  useEffect(() => {
    gsap.set("#content-container", {
      opacity: 1,
      duration: 1,
      onComplete: () => componentTl.play(),
    })
  }, [currentSegment])

  let ribbon = null
  switch ((2020 - 1 - years[currentSegment]).toString()) {
    case "5":
      ribbon = FiveYearRibbon
      break
    case "10":
      ribbon = TenYearRibbon
      break
    case "15":
      ribbon = FifteenYearRibbon
      break

    case "20":
      ribbon = TwentyYearRibbon
      break
    case "25":
      ribbon = TwentyfiveYearRibbon
      break
    case "30":
      ribbon = ThirtyYearRibbon
      break
    case "35":
      ribbon = ThirtyfiveYearRibbon
      break
    case "40":
      ribbon = FortyYearRibbon
      break
    case "45":
      ribbon = FortyfiveYearRibbon
      break
    default:
      ribbon = null
  }

  /**
   * Build the components on their respective timelines
   * @return {array} Array of components on timeline to render
   */
  const buildTimeline = () => {
    let componentList = []

    if (!isNaN(parseInt(years[currentSegment]))) {
      // First the year of hire
      componentList.push(
        <FlyingIn
          key={`component-${years[currentSegment]}-${currentSegment}`}
          tl={componentTl}
          label={years[currentSegment]}
          animationSettings={animationSettings}
          className='card--title-year--anim'
        >
          <h1 className='card--title-year'>{years[currentSegment]}</h1>
        </FlyingIn>
      )

      // Then an anniversary card
      componentList.push(
        <FlyingIn
          key={`component-${years[currentSegment]}-${currentSegment}`}
          tl={componentTl}
          label={years[currentSegment]}
          animationSettings={animationSettings}
          className='card--anniversary-year--anim'
          heroTl={heroTl}
        >
          <div className='card--anniversary-year'>
            <div className='card--anniversary-year__ribbon'>
              <img src={ribbon} alt='ribbon' />
            </div>
            <div className='card--anniversary-year__copy'>
              <p>{2020 - years[currentSegment] - 1} Year Service Awards</p>
            </div>
          </div>
        </FlyingIn>
      )
    }
    // Then the CEO Hero Video with supporting content
    componentList.push(
      <HeroVideo
        currentSegment={currentSegment}
        scrubTo={scrubTo}
        handleHeroLoad={handleHeroLoad}
        handleHeroComplete={handleHeroComplete}
        heroTl={heroTl}
        cleaningUp={cleaningUp}
        bookend={isNaN(parseInt(years[currentSegment]))}
        setMusicPlaying={setMusicPlaying}
        setPlayer={setPlayer}
      />
    )
    if (!isNaN(parseInt(years[currentSegment]))) {
      // Then the list of honorees for that year
      const peopleList = people
        .filter(
          (ea) =>
            parseInt(ea.yearsCelebrating) === 2020 - years[currentSegment] - 1
        )
        .sort((a, b) => {
          if (a.lastName > b.lastName) {
            return 1
          } else if (a.lastName < b.lastName) {
            return -1
          } else {
            if (a.firstName > b.firstName) {
              return 1
            } else if (a.firstName < b.firstName) {
              return -1
            } else {
              return 0
            }
          }
        })
        .map((person) => {
          return {
            type: "ee",
            value: person,
          }
        })
      componentList.push(
        <List
          list={{ [2020 - years[currentSegment] - 1]: peopleList }}
          currentSegment={currentSegment}
          // className='anniversary-list--anim'
          scrubTo={scrubTo}
        />
      )
    }
    return componentList
  }

  /**
   * Fade in the list for that year
   * @param {string || object} identifier class or id string, or ref object to identify list to animate
   */
  const showItem = (identifier) => {
    gsap.to(identifier, {
      opacity: 1,
      duration: 1,
      ease: "power1.inOut",
    })
  }

  /**
   * Fade out the list for that year
   * @param {string || object} identifier class or id string, or ref object to identify list to animate
   */
  const hideItem = (identifier) => {
    gsap.set(identifier, {
      opacity: 0,
      duration: 0.5,
    })
  }

  /**
   * Pause component timeline when Hero video finishes loading
   */
  const handleHeroLoad = () => {
    console.log("Hero Loaded")
  }

  /**
   * Bring in the anniversary list when hero video completes and cue the music
   */
  const handleHeroComplete = () => {
    console.log("Hero Complete")
    showItem(".anniversary-list--anim")
    setShowControls(false)
    setShowAdvancer(true)
  }

  /**
   * Clean up the currently playing timelines and 'jump' to a new segment
   * @param {number} year Year clicked on
   */
  const scrubTo = (year) => {
    console.log(year)
    const segmentIndex = years.findIndex((ea) => ea === year)
    // gsap.globalTimeline.restart()
    gsap.to("#content-container", {
      onStart: () => {
        hideItem(".anniversary-list--anim")
        // hideItem(".hero-video__container")
        setMusicPlaying(false)
        setShowControls(true)
        setShowAdvancer(false)
      },
      opacity: 0,
      duration: 0.5,
      onComplete: () => {
        // gsap.set(".hero-video__container", {
        //   scale: 1,
        //   opacity: 1,
        //   top: "50%",
        //   left: "50%",
        // })
        if (segmentIndex !== currentSegment) {
          setCurrentSegment(segmentIndex)
        } else {
          restartSegment(vidPlayer)
        }
      },
    })
  }

  const restartSegment = (vidPlayer) => {
    vidPlayer.currentTime(0)
    vidPlayer.pause()
    gsap.globalTimeline.pause(0, true)
    
    gsap.globalTimeline.play()
    gsap.to("#content-container", {
      opacity: 1,
      duration: 0.5,
      onComplete: () => componentTl.play(),
    })
    gsap.set(".hero-video__container", {
      scale: 1,
      opacity: 1,
      top: "50%",
      left: "50%",
    })
    heroTl.play()
    vidPlayer.play()
  }

  return (
    <div className='app-container'>
      <NavBar musicPlaying={musicPlaying} currentSegment={currentSegment} />
      <div id='content-container'>{buildTimeline()}</div>
      <div className='timeline-controls'>
        {Object.values(vidPlayer).length > 0 && showControls ? (
          vidPlayerPaused ? (
            <FaPlayCircle
              onClick={() => {
                gsap.globalTimeline.play()
                vidPlayer.play()
                setVidPlayerPaused(false)
              }}
            />
          ) : (
            <FaPauseCircle
              onClick={() => {
                gsap.globalTimeline.pause()
                vidPlayer.pause()
                setVidPlayerPaused(true)
              }}
            />
          )
        ) : null}

        {typeof currentSegment === "number" && showAdvancer && (
          <div
            className='anniversary-list__advance'
            onClick={() => {
              scrubTo(years[currentSegment + 1])
              setShowAdvancer(false)
              setShowControls(true)
            }}
          >
            <span>To {years[currentSegment + 1]} {`>`}</span>
          </div>
        )}
      </div>

      <div id='progress-bar'>
        <Progress
          currentSegment={currentSegment}
          scrubTo={scrubTo}
          sampleData={sampleData}
        />
      </div>
    </div>
  )
}

export default Timeline
